import { useState, useEffect } from 'react';
import { usersService } from '../services/usersService';

export function useUserEmail(userId: string | undefined): string | null {
  const [email, setEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!userId) {
      setEmail(null);
      return;
    }

    const resolveEmail = async () => {
      setIsLoading(true);
      try {
        const userEmail = await usersService.getUserEmail(userId);
        setEmail(userEmail);
      } catch (error) {
        console.error('Failed to resolve user email:', error);
        setEmail(null);
      } finally {
        setIsLoading(false);
      }
    };

    resolveEmail();
  }, [userId]);

  return isLoading ? '...' : email;
}

export function useUserEmails(userIds: (string | undefined)[]): Record<string, string | null> {
  const [emails, setEmails] = useState<Record<string, string | null>>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const validUserIds = userIds.filter(Boolean) as string[];
    
    if (validUserIds.length === 0) {
      setEmails({});
      return;
    }

    const resolveEmails = async () => {
      setIsLoading(true);
      try {
        const results: Record<string, string | null> = {};
        
        // Resolve all emails in parallel
        await Promise.all(
          validUserIds.map(async (userId) => {
            const email = await usersService.getUserEmail(userId);
            results[userId] = email;
          })
        );
        
        setEmails(results);
      } catch (error) {
        console.error('Failed to resolve user emails:', error);
        setEmails({});
      } finally {
        setIsLoading(false);
      }
    };

    resolveEmails();
  }, [JSON.stringify(userIds)]); // Use JSON.stringify for array comparison

  return emails;
}